import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Space, Table, Popconfirm, message, Tooltip, Tag, Upload, Modal } from 'antd';
import { DeleteOutlined, SearchOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import InputRules from '../../data/InputRules';
import { connect } from 'react-redux';
import { Actions } from '../../store/actions';
import ReactImage from '../../components/Image';
import { toFormData } from '../../util/FormData';

const layout = {
	labelCol: {
		span: 6,
	},
	wrapperCol: {
		span: 6,
	},
};
const tailLayout = {
	wrapperCol: {
		offset: 6,
		span: 16,
	},
};

const uploadButton = (
	<div>
		<PlusOutlined />
		<div style={{ marginTop: 8 }}>Upload</div>
	</div>
);

const MakeManagement = ({ makes, fetchMakes, addMake, deleteMake, updateMake, updateMakeImage }) => {
	const [searchText, setSearchText] = useState('');
	const [searchedColumn, setSearchedColumn] = useState('');
	const [filelist, setFileList] = useState([]);
	const [imageList, setImageList] = useState([]);
	const [editModal, setEditModal] = useState(false);
	const [editMake, setEditMake] = useState();

	useEffect(() => {
		fetchMakes();
	}, []);

	useEffect(() => {
		if (filelist.length) {
			const file = filelist[filelist.length - 1].originFileObj;
			file.thumbUrl = URL.createObjectURL(file);
			const list = imageList;
			list.push(file);
			setImageList(list);
		}
	}, [filelist]);

	const getColumnSearchProps = (dataIndex) => ({
		filterDropdown: function filterDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
			return (
				<div style={{ padding: 8 }}>
					<Input
						placeholder={`Search ${dataIndex}`}
						value={selectedKeys[0]}
						onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
						onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
						style={{ width: 188, marginBottom: 8, display: 'block' }}
					/>
					<Space>
						<Button
							type="primary"
							onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
							icon={<SearchOutlined />}
							size="small"
							style={{ width: 90 }}
						>
							Search
						</Button>
						<Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
							Reset
						</Button>
					</Space>
				</div>
			);
		},
		filterIcon: function filter(filtered) {
			return <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />;
		},
		onFilter: (value, record) =>
			record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
		render: function render(text) {
			return searchedColumn === dataIndex ? (
				<Highlighter
					highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
					searchWords={[searchText]}
					autoEscape
					textToHighlight={text ? text.toString() : ''}
				/>
			) : (
				text
			);
		},
	});

	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm();
		setSearchText(selectedKeys[0]);
		setSearchedColumn(dataIndex);
	};

	const handleReset = (clearFilters) => {
		clearFilters();
		setSearchText('');
	};

	const onFinish = async (values) => {
		values.image = imageList[0];
		const data = toFormData(values);
		await addMake(data);
		await fetchMakes();
	};

	const onEditFinish = async (values) => {
		values.image = imageList[0];
		const data = toFormData(values);
		await updateMakeImage(editMake._id, data);
		await fetchMakes();
	};

	const onDelete = async (id) => {
		deleteMake(id);
		await fetchMakes();
	};

	const onFinishFailed = (errorInfo) => {
		message.error('Something went wrong!');
	};

	const columns = [
		{
			title: 'Image',
			dataIndex: 'image',
			key: 'image',
			render: function image(text, record) {
				return (
					<Space size="middle">
						<ReactImage src={record.image} width={50} />
					</Space>
				);
			},
		},
		{
			title: 'Make',
			dataIndex: '_id',
			key: '_id',
			sorter: (a, b) => {
				if (a._id.toLowerCase() < b._id.toLowerCase()) return -1;
				if (a._id.toLowerCase() > b._id.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('_id'),
		},
		{
			title: 'Make Count',
			dataIndex: 'makecount',
			key: 'makecount',
			render: function keywords(text, record) {
				return record?.makecount?.map((keyword) => <Tag key={keyword}>{keyword}</Tag>);
			},
		},
		{
			title: 'Action',
			key: 'action',
			render: function action(text, record) {
				return (
					<Space size="middle">
						<EditOutlined
							onClick={() => {
								setEditMake(record);
								setEditModal(!editModal);
							}}
						/>
						<Tooltip title="Delete Make">
							<Popconfirm
								title="Are you sure to delete this make?"
								onConfirm={() => onDelete(record._id)}
								okText="Yes"
								cancelText="No"
							>
								<DeleteOutlined />
							</Popconfirm>
						</Tooltip>
					</Space>
				);
			},
		},
	];

	const handleChange = ({ fileList }) => {
		setFileList(fileList);
	};

	return (
		<div>
			<div>
				<Form
					{...layout}
					name="make-form"
					initialValues={{ remember: true }}
					onFinish={onFinish}
					onFinishFailed={onFinishFailed}
				>
					<Form.Item label="Upload Image" name="image" rules={InputRules.required('Please enter image!')}>
						<Upload
							accept=".jpg,.png,.jpeg"
							listType="picture-card"
							onChange={handleChange}
							fileList={filelist}
							beforeUpload={() => false}
							maxCount={1}
						>
							{uploadButton}
						</Upload>
					</Form.Item>
					<Form.Item label="Make" name="make" rules={InputRules.required('Please input make!')}>
						<Input />
					</Form.Item>
					<Form.Item {...tailLayout}>
						<Button type="primary" htmlType="submit">
							Add Make
						</Button>
					</Form.Item>
				</Form>
			</div>
			<div>
				<Table dataSource={makes} columns={columns} rowKey={(record) => record._id} size="small" scroll={{ x: true }} />
			</div>
			<Modal
				title={`Edit ${editMake?._id}`}
				centered={true}
				visible={editModal}
				footer={null}
				onCancel={() => setEditModal(false)}
				destroyOnClose={true}
			>
				<Form
					{...layout}
					name="make-form-edit"
					initialValues={{ remember: true }}
					onFinish={onEditFinish}
					onFinishFailed={onFinishFailed}
				>
					<Form.Item label="Upload Image" name="image" rules={InputRules.required('Please enter image!')}>
						<Upload
							accept=".jpg,.png,.jpeg"
							listType="picture-card"
							onChange={handleChange}
							fileList={filelist}
							beforeUpload={() => false}
							maxCount={1}
						>
							{uploadButton}
						</Upload>
					</Form.Item>
					<Form.Item {...tailLayout}>
						<Button type="primary" htmlType="submit">
							Update Image
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

const mapStateToProps = (store) => ({
	makes: store.vehicleMake.makes,
});

const mapDispatchToProps = (dispatch) => ({
	fetchMakes: () => dispatch(Actions.fetchMakes()),
	addMake: (body) => dispatch(Actions.addMake(body)),
	deleteMake: (id) => dispatch(Actions.deleteMake(id)),
	updateMake: (id, body) => dispatch(Actions.updateMake(id, body)),
	updateMakeImage: (id, body) => dispatch(Actions.updateMakeImage(id, body)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MakeManagement);
